// Typed HTTP error and RFC-7807 problem plumbing for the fetch chokepoint.
//
// HttpError is what `fetchHelper` (services.tsx) throws on any non-OK, non-401
// response. It is deliberately backward-compatible with every existing catch
// site: it `extends Error`, and `message` stays the raw response body text, so
// `err instanceof Error` and `JSON.parse(err.message)` keep working exactly as
// before. The `status` and `problem` fields are purely additive.

// RFC-7807 problem+json body. ACH emits these (e.g. the writes-disabled 503
// `{title,status,detail}` from the WriteGate middleware). Extra keys are allowed
// (handler-level ProblemError adds `errors`/`field_errors`).
export type ProblemDetails = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
};

export class HttpError extends Error {
  readonly status: number;

  readonly problem?: ProblemDetails;

  constructor(status: number, body: string, problem?: ProblemDetails) {
    // message stays the raw body text — backward-compatible with every existing
    // `JSON.parse(error.message)` / `error.message` catch site.
    super(body);
    this.name = 'HttpError';
    this.status = status;
    this.problem = problem;
    // Restore prototype chain so `instanceof HttpError` works after transpile.
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

// parseProblem attempts to read a JSON/problem+json error body into a
// ProblemDetails object. Returns undefined for empty bodies, non-JSON content
// types, or unparseable/non-object bodies (the caller falls back to raw text).
export const parseProblem = (contentType: string | null, body: string): ProblemDetails | undefined => {
  if (!body) return undefined;
  const ct = (contentType || '').toLowerCase();
  // Matches both `application/json` and `application/problem+json`.
  if (!ct.includes('json')) return undefined;
  try {
    const parsed = JSON.parse(body);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as ProblemDetails) : undefined;
  } catch {
    return undefined;
  }
};

// DOM CustomEvent used to surface ACH-backend request failures to the user in an
// attributable way (see SnackBarProvider listener). Only dispatched for failures
// against an ACH host, so Ventura/Rails call sites are wholly unaffected.
export const ACH_REQUEST_ERROR_EVENT = 'ach:request-error';

export type AchRequestErrorDetail = {
  status: number;
  problem?: ProblemDetails;
  message: string;
  url: string;
  // true only for the ACH WriteGate 503 writes-disabled response.
  writesDisabled: boolean;
};

export const dispatchAchRequestError = (detail: AchRequestErrorDetail): void => {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent<AchRequestErrorDetail>(ACH_REQUEST_ERROR_EVENT, { detail }));
  }
};

// isWritesDisabled recognises the ACH WriteGate 503 (problem+json whose detail
// names the writes-disabled kill-switch), distinct from a generic 503 outage.
export const isWritesDisabled = (status: number, problem?: ProblemDetails): boolean =>
  status === 503 && typeof problem?.detail === 'string' && problem.detail.toLowerCase().includes('writes are disabled');
